import type { Spritesheet, Texture } from "pixi.js";
import type { AssetRegistry } from "./AssetRegistry";
import { SHELL_BUNDLE_NAMES } from "../boot/defaults/BundleNames";
import type { Log } from "@slot/presentation";

/**
 * Everything the shell chrome draws with, resolved ONCE from the `shell-ui` bundle: the
 * `gameControlUI` sheet (29 frames) and the desktop bar art the DOM chrome paints, and the
 * grid-loading frames. Missing art resolves to null and is logged so the game still boots without
 * it. The bundle's square, border nine-slices and glow are not resolved here: the shell draws none of
 * them, and a title that does reads them through `BrandedAssetResolver.getFromBundleOrNull`.
 */
export interface ShellUiAssets {
  /** The control atlas image and the desktop bar image, as URLs the DOM chrome can paint. */
  readonly atlasUrl: string | null;
  readonly desktopBarUrl: string | null;
  /** A `gameControlUI` frame by name (`spinBtnBack`, `autoBetIcon`, …); null when missing. */
  frame(name: string): Texture | null;
  readonly desktopBar: Texture | null;
  /** `loadingAnimation` frames "1".."30" in order. */
  readonly loadingFrames: readonly Texture[];
}

export function loadShellUiAssets(assets: AssetRegistry, log: Log): ShellUiAssets {
  const bundle = SHELL_BUNDLE_NAMES.shellUi;
  const optional = <T>(path: string, what: string): T | null => {
    const asset = assets.getLoadedAssetOrNull<T>(bundle, path);
    if (asset === null) log(`[ShellUiAssets] ${what} "${path}" not loaded`);
    return asset;
  };
  const sheet = optional<Spritesheet>("sprites/gameControlUI", "control atlas");
  const loadingSheet = optional<Spritesheet>("sprites/essentials/loadingAnimation/loadingAnimation", "loading sheet");
  const loadingFrames = loadingSheet
    ? Object.keys(loadingSheet.textures)
        .sort((a, b) => Number(a) - Number(b))
        .map((key) => loadingSheet.textures[key]!)
    : [];
  const missingFrames = new Set<string>();
  return {
    atlasUrl: assets.resolveUrl(bundle, "sprites/gameControlUI"),
    desktopBarUrl: assets.resolveUrl(bundle, "sprites/desktopBar"),
    frame(name: string): Texture | null {
      const texture = sheet?.textures[name] ?? null;
      if (!texture && !missingFrames.has(name)) {
        missingFrames.add(name);
        log(`[ShellUiAssets] gameControlUI frame "${name}" missing`);
      }
      return texture;
    },
    desktopBar: optional<Texture>("sprites/desktopBar", "texture"),
    loadingFrames,
  };
}
